import formatDate from '../../helpers/formatDate';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

export interface SelectFilterOption {
  value: string;
  label: string;
}

export interface SelectFilterField {
  type: 'select';
  key: string;
  label: string;
  placeholder?: string;
  options: SelectFilterOption[];
}

export interface MultiSelectFilterField {
  type: 'multiSelect';
  key: string;
  label: string;
  placeholder?: string;
  options: SelectFilterOption[];
}

export interface DateRangeFilterField {
  type: 'dateRange';
  startKey: string;
  endKey: string;
  label: string;
}

export type FilterField = SelectFilterField | MultiSelectFilterField | DateRangeFilterField;

interface ServerSideFilterPopoverProps<T extends Record<string, unknown>> {
  filters: T;
  onFiltersChange: (filters: T) => void;
  clearAllFilters: () => void;
  fields: FilterField[];
}

export function ServerSideFilterPopover<T extends Record<string, unknown>>({
  filters,
  onFiltersChange,
  clearAllFilters,
  fields,
}: ServerSideFilterPopoverProps<T>) {
  const handleSelectChange = (key: string, value: string, options: SelectFilterOption[]) => {
    // Check if this is the "all" option (value not in options means clear the filter)
    const isValidOption = options.some((opt) => opt.value === value);
    if (!isValidOption) {
      // Remove the filter key
      const { [key]: _, ...rest } = filters;
      onFiltersChange(rest as T);
    } else {
      onFiltersChange({
        ...filters,
        [key]: value === 'true' ? true : value === 'false' ? false : value,
      });
    }
  };

  const handleMultiSelectChange = (key: string, values: string[]) => {
    if (values.length === 0) {
      const { [key]: _, ...rest } = filters;
      onFiltersChange(rest as T);
    } else {
      onFiltersChange({ ...filters, [key]: values });
    }
  };

  const getMultiSelectValue = (key: string): string[] => {
    const value = filters[key];
    return Array.isArray(value) ? (value as string[]) : [];
  };

  const handleDateChange = (key: string, date: Date | null) => {
    if (date) {
      onFiltersChange({
        ...filters,
        [key]: formatDate(date),
      });
    } else {
      const { [key]: _, ...rest } = filters;
      onFiltersChange(rest as T);
    }
  };

  const getSelectValue = (key: string): string | undefined => {
    const value = filters[key];
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (typeof value === 'string') return value;
    return undefined;
  };

  const getDateValue = (key: string): Date | null => {
    const value = filters[key];
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    return null;
  };

  return (
    <FilterPopoverContent clearAllFilters={clearAllFilters}>
      {fields.map((field) => {
        if (field.type === 'select') {
          return (
            <div key={field.key}>
              <Label className="mb-2 block">{field.label}</Label>
              <Select
                value={getSelectValue(field.key)}
                onValueChange={(value) => handleSelectChange(field.key, value, field.options)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}...`} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }

        if (field.type === 'multiSelect') {
          const selectedValues = getMultiSelectValue(field.key);
          return (
            <div key={field.key}>
              <Label className="mb-2 block">{field.label}</Label>
              <MultiSelect
                values={selectedValues}
                onValuesChange={(newValues) => handleMultiSelectChange(field.key, newValues)}
              >
                <MultiSelectTrigger className="w-full">
                  <MultiSelectValue placeholder={field.placeholder ?? `Select ${field.label.toLowerCase()}...`} />
                </MultiSelectTrigger>
                <MultiSelectContent>
                  <MultiSelectGroup>
                    {field.options.map((option) => (
                      <MultiSelectItem key={option.value} value={option.value}>
                        {option.label}
                      </MultiSelectItem>
                    ))}
                  </MultiSelectGroup>
                </MultiSelectContent>
              </MultiSelect>
            </div>
          );
        }

        if (field.type === 'dateRange') {
          return (
            <div key={`${field.startKey}-${field.endKey}`}>
              <Label className="mb-2 block">{field.label}</Label>
              <DateRangeFilter
                startDate={getDateValue(field.startKey)}
                endDate={getDateValue(field.endKey)}
                setStartDate={(date) => handleDateChange(field.startKey, date)}
                setEndDate={(date) => handleDateChange(field.endKey, date)}
              />
            </div>
          );
        }

        return null;
      })}
    </FilterPopoverContent>
  );
}
